import React, { useEffect, useRef, useState } from 'react';
import { Layout, Menu, Dropdown, Avatar, Space, Tag, notification, Result, Button } from 'antd';
import {
  DashboardOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  FormOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TeamOutlined,
  PictureOutlined,
  FileTextOutlined,
  ShopOutlined,
  ProfileOutlined,
  MessageOutlined,
  IdcardOutlined,
  LogoutOutlined,
  SoundOutlined,
  GiftOutlined,
  ShareAltOutlined,
  SafetyCertificateOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import dayjs from 'dayjs';
import request, { getToken, clearToken } from '../utils/request';
import { playNotifySound } from '../utils/notify';

const { Header, Sider, Content } = Layout;

// 图标名 -> 组件注册表：菜单存于数据库的只是图标名（icon 字段），渲染时映射为 antd 图标
const ICON_REGISTRY: Record<string, React.ReactNode> = {
  DashboardOutlined: <DashboardOutlined />,
  MedicineBoxOutlined: <MedicineBoxOutlined />,
  UserOutlined: <UserOutlined />,
  FormOutlined: <FormOutlined />,
  ShoppingOutlined: <ShoppingOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  TeamOutlined: <TeamOutlined />,
  PictureOutlined: <PictureOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  ShopOutlined: <ShopOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  MessageOutlined: <MessageOutlined />,
  IdcardOutlined: <IdcardOutlined />,
  SoundOutlined: <SoundOutlined />,
  GiftOutlined: <GiftOutlined />,
  ShareAltOutlined: <ShareAltOutlined />,
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  MenuUnfoldOutlined: <MenuUnfoldOutlined />,
};

type MenuNode = { id: number; key: string; name: string; icon?: string | null; type: string; children?: MenuNode[] };

// 服务端菜单树 -> antd Menu items（GROUP 作子菜单，MENU 作可点击项，key=路由）
const toMenuItems = (nodes: MenuNode[]): any[] =>
  nodes.map((n) => ({
    key: n.key,
    icon: n.icon ? ICON_REGISTRY[n.icon] : undefined,
    label: n.name,
    ...(n.type === 'GROUP' ? { children: toMenuItems(n.children || []) } : {}),
  }));

// 收集叶子（MENU）路由，供前端路由守卫判定当前路径是否可访问
const collectLeafKeys = (nodes: MenuNode[]): string[] =>
  nodes.flatMap((n) => (n.type === 'GROUP' ? collectLeafKeys(n.children || []) : [n.key]));

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState<any>(null);
  const [menuTree, setMenuTree] = useState<MenuNode[]>([]);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [waitingCount, setWaitingCount] = useState<number | null>(null);
  // 今日累计取号数基线：首次轮询只记录，之后计数变大才提醒（当日挂号记录不删除，总数只增不减）
  const prevIssued = useRef<number | null>(null);
  // 在途互斥：上一轮没回来就不叠加发请求，避免慢网下请求堆积、提醒重复弹
  const pollInFlight = useRef(false);
  const [todo, setTodo] = useState<{ pendingConfirm: number; pendingWithdraw: number } | null>(null);
  // 待办数量基线：首次轮询只记录，之后数量增加才提醒
  const prevTodo = useRef<{ pendingConfirm: number; pendingWithdraw: number } | null>(null);

  useEffect(() => {
    if (getToken()) {
      request
        .get('/auth/admin-profile')
        .then((res: any) => setAdmin(res))
        .catch(() => {});
    }
  }, []);

  // 拉取当前管理员可见菜单树（超管=全部启用项，普通=角色授权项）
  useEffect(() => {
    if (!getToken()) return;
    request
      .get('/admin/menu/mine')
      .then((res: any) => setMenuTree(res || []))
      .catch(() => setMenuTree([]))
      .finally(() => setMenuLoaded(true));
  }, []);

  // 新挂号提醒：每 20 秒探测今日挂号数 + 待办数
  // 只打无副作用的列表计数接口（page=1&pageSize=1 取 total），不再打 /registration/today ——
  // 该 GET 内部会 expireOutdated 批量作废往日挂号 + upsert 发号设置，与今日挂号页的 15 秒轮询
  // 叠加等于两个定时器互相触发写副作用
  useEffect(() => {
    if (!getToken()) return;
    const poll = async () => {
      if (pollInFlight.current) return;
      pollInFlight.current = true;
      const today = dayjs().format('YYYY-MM-DD');
      try {
        const [issued, pending]: any[] = await Promise.all([
          request.get('/registration', { params: { page: 1, pageSize: 1, startDate: today, endDate: today } }),
          request.get('/registration', {
            params: { page: 1, pageSize: 1, status: 'PENDING', startDate: today, endDate: today },
          }),
        ]);
        setWaitingCount(Number(pending?.total) || 0);
        const cur = Number(issued?.total) || 0;
        if (prevIssued.current !== null && cur > prevIssued.current) {
          playNotifySound();
          notification.info({
            message: '新挂号提醒',
            description: `今日新增 ${cur - prevIssued.current} 人挂号，请前往「今日挂号」处理`,
            placement: 'topRight',
            duration: 6,
          });
        }
        prevIssued.current = cur;
      } catch {
        // 轮询失败静默忽略，下个周期重试
      }
      try {
        const t: any = await request.get('/order/admin/todo-count');
        const cur = {
          pendingConfirm: Number(t?.pendingConfirm) || 0,
          pendingWithdraw: Number(t?.pendingWithdraw) || 0,
        };
        setTodo(cur);
        if (prevTodo.current) {
          if (cur.pendingConfirm > prevTodo.current.pendingConfirm) {
            playNotifySound();
            notification.warning({
              message: '新增待核对付款凭证',
              description: `当前有 ${cur.pendingConfirm} 笔订单待核对，请前往订单处理`,
              placement: 'topRight',
              duration: 6,
            });
          }
          if (cur.pendingWithdraw > prevTodo.current.pendingWithdraw) {
            playNotifySound();
            notification.warning({
              message: '新增提现申请',
              description: `当前有 ${cur.pendingWithdraw} 笔提现待处理，请前往提现管理`,
              placement: 'topRight',
              duration: 6,
            });
          }
        }
        prevTodo.current = cur;
      } catch {
        // 忽略
      } finally {
        pollInFlight.current = false;
      }
    };
    poll();
    const timer = setInterval(poll, 20000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    clearToken();
    navigate('/login', { replace: true });
  };

  // 菜单由后端驱动：未加载完成或无可见菜单时为空；仅叶子（以 / 开头的路由）可点击跳转
  const menuItems = toMenuItems(menuTree);
  const leafKeys = new Set(collectLeafKeys(menuTree));
  // 路由守卫：超管恒放行；普通管理员仅能进入其菜单叶子（含首页）；admin 未加载前不拦截避免闪屏
  const denied =
    !!admin && menuLoaded && !admin.isSuper && location.pathname !== '/' && !leafKeys.has(location.pathname);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider theme="dark" style={{ background: '#065F46' }}>
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ color: '#fff', margin: 0, fontSize: 18 }}>挂号管理系统</h2>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          style={{ background: '#065F46' }}
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            if (key.startsWith('/')) navigate(key);
          }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
            <div>
              {waitingCount !== null && (
                <Tag color={waitingCount > 0 ? 'orange' : 'default'} style={{ fontSize: 13, padding: '2px 10px' }}>
                  今日待诊：{waitingCount} 人
                </Tag>
              )}
              {todo !== null && (
                <Tag
                  color={todo.pendingConfirm > 0 ? 'red' : 'default'}
                  style={{ fontSize: 13, padding: '2px 10px', cursor: 'pointer' }}
                  onClick={() => navigate('/orders')}
                >
                  待核对：{todo.pendingConfirm}
                </Tag>
              )}
              {todo !== null && (
                <Tag
                  color={todo.pendingWithdraw > 0 ? 'gold' : 'default'}
                  style={{ fontSize: 13, padding: '2px 10px', cursor: 'pointer' }}
                  onClick={() => navigate('/withdraws')}
                >
                  待提现：{todo.pendingWithdraw}
                </Tag>
              )}
            </div>
            <Dropdown
              menu={{
                items: [
                  { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
                ],
              }}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" icon={<UserOutlined />} style={{ background: '#065F46' }} />
                <span>{admin?.name || '管理员'}</span>
                {admin?.roleName && (
                  <Tag color={admin?.isSuper ? 'red' : 'blue'} style={{ marginInlineEnd: 0 }}>
                    {admin.roleName}
                  </Tag>
                )}
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: '#fff', borderRadius: 8 }}>
          {denied ? (
            <Result
              status="403"
              title="403"
              subTitle="你没有访问该页面的权限，请联系超级管理员在「角色管理」中为你的角色授权该菜单"
              extra={
                <Button type="primary" onClick={() => navigate('/', { replace: true })}>
                  返回首页
                </Button>
              }
            />
          ) : (
            <Outlet />
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
